import React from 'react';
import { Wallet, XCircle, RefreshCcw, PenLine, AlertTriangle, CheckCircle, Clock, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const RefundPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-pink-50/30">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-12 pt-24">
        {/* Icon + Title */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
            <Wallet className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-purple-700">
            Refund Policy
          </h1>
          <p className="text-gray-500 text-sm mb-1">Last Updated: March 2026</p>
          <p className="text-gray-500 text-sm">Please read our refund policy carefully before making any payments</p>
        </div>

        {/* Critical Notice */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <p className="font-bold text-red-700 mb-1">CRITICAL: MEMBERSHIP IS NON-REFUNDABLE</p>
            <p className="text-red-700 text-sm leading-relaxed">
              All KoPartner membership fees (6 months, 1 year, or lifetime) are <strong>STRICTLY NON-REFUNDABLE</strong> under any circumstances. By purchasing a membership, you agree to this policy without exception.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* 1. KoPartner Membership - NO REFUNDS */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-red-700 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              1. KoPartner Membership - NO REFUNDS
            </h2>
            <p className="text-red-800 text-sm font-semibold mb-3">
              Membership fees are NON-REFUNDABLE under ANY circumstance, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-1 text-red-800 text-sm pl-1 mb-4">
              <li>Change of mind after purchase</li>
              <li>Inability to find clients or bookings</li>
              <li>Dissatisfaction with Platform features</li>
              <li>Account suspension or termination (for any reason)</li>
              <li>Technical issues or Platform downtime</li>
              <li>Moving to a different location</li>
              <li>Health issues or personal circumstances</li>
              <li>Voluntary discontinuation of services</li>
              <li>Any other reason whatsoever</li>
            </ul>
            <div className="bg-red-100 border border-red-300 rounded-xl p-4">
              <p className="text-red-800 text-sm font-semibold">
                By purchasing a membership, you irrevocably agree that the fee is a non-refundable platform access fee and you waive any right to claim a refund.
              </p>
            </div>
          </div>

          {/* 2. Service Booking Refunds - Wallet Only */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-purple-600" />
              2. Service Booking Refunds - Wallet Only
            </h2>
            <p className="text-gray-700 text-sm mb-4">For service bookings made by Clients, the following refund policy applies:</p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
              <p className="text-amber-800 font-bold text-sm mb-1">IMPORTANT: Refunds to KoPartner Wallet ONLY</p>
              <p className="text-amber-800 text-sm">
                All eligible refunds will be credited to your <strong>KoPartner Wallet</strong> only. No refunds will be made to bank accounts, UPI, credit cards, or any other payment method. Wallet balance can only be used for future bookings on the Platform.
              </p>
            </div>

            <p className="font-semibold text-gray-800 text-sm mb-3">Cancellation by Client:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800 text-sm">24+ Hours Before</p>
                  <p className="text-green-700 text-sm">100% refund to Wallet</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 text-sm">12-24 Hours Before</p>
                  <p className="text-amber-700 text-sm">50% refund to Wallet</p>
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-800 text-sm">Less than 12 Hours</p>
                  <p className="text-orange-700 text-sm">No refund</p>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2">
                <X className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 text-sm">No Show</p>
                  <p className="text-red-700 text-sm">No refund</p>
                </div>
              </div>
            </div>

            <p className="font-semibold text-gray-800 text-sm mb-2">Cancellation by KoPartner:</p>
            <p className="text-gray-700 text-sm">
              If a KoPartner cancels a confirmed booking, the Client will receive a 100% refund to their KoPartner Wallet.
            </p>
          </section>

          {/* 3. KoPartner Wallet Terms */}
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-purple-800 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-700" />
              3. KoPartner Wallet Terms
            </h2>
            <p className="text-purple-900 text-sm mb-3">The KoPartner Wallet is a virtual wallet for storing refund credits. Important terms:</p>
            <ul className="list-disc list-inside space-y-1 text-purple-900 text-sm pl-1">
              <li><strong>Non-Transferable:</strong> Wallet balance cannot be transferred to another user or account</li>
              <li><strong>Non-Withdrawable:</strong> Wallet balance cannot be withdrawn to bank accounts or other payment methods</li>
              <li><strong>Platform Use Only:</strong> Balance can only be used for future service bookings on KoPartner</li>
              <li><strong>No Cash Value:</strong> Wallet credits have no cash value and cannot be exchanged for cash</li>
              <li><strong>No Expiry:</strong> Wallet balance does not expire as long as your account is active</li>
              <li><strong>Account Termination:</strong> Wallet balance is forfeited upon account termination or suspension</li>
            </ul>
          </div>

          {/* 4. Payment Security & Cyber Fraud Disclaimer */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-blue-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-blue-700" />
              4. Payment Security &amp; Cyber Fraud Disclaimer
            </h2>
            <p className="text-blue-900 font-bold text-sm mb-3">IMPORTANT NOTICE REGARDING PAYMENTS:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-900 text-sm pl-1 mb-4">
              <li>All payments are made voluntarily by Users at their own discretion and choice</li>
              <li>KoPartner does NOT collect, store, or process any payment card details, bank account information, or UPI credentials</li>
              <li>All payments are processed through secure third-party payment gateways or QR-based payments</li>
              <li>Users verify payments by uploading screenshots which are analyzed by AI</li>
              <li>KoPartner is NOT responsible for any payment errors, unauthorized transactions, or disputes with payment providers</li>
            </ul>
            <div className="bg-blue-100 border border-blue-300 rounded-xl p-4">
              <p className="text-blue-900 text-sm font-semibold">
                CYBER FRAUD DISCLAIMER: Since KoPartner does not collect, store, or process any financial information, no claims of cyber fraud can be made against the Platform. All payments are voluntary choices made by Users who accept full responsibility for their transactions.
              </p>
            </div>
          </div>

          {/* 5. How to Request a Refund */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900">5. How to Request a Refund</h2>
            <p className="text-gray-700 text-sm mb-3">To request an eligible refund for service bookings:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm pl-1 mb-4">
              <li>Log in to your KoPartner account</li>
              <li>Go to "My Bookings" section</li>
              <li>Select the booking you wish to cancel</li>
              <li>Click "Cancel Booking" and confirm</li>
              <li>Eligible refunds will be automatically credited to your KoPartner Wallet within 24-48 hours</li>
            </ol>
            <p className="text-gray-600 text-sm">
              For any issues or disputes, contact us at support@kopartner.in
            </p>
          </section>

          {/* 6. Contact Information */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900">6. Contact Information</h2>
            <p className="font-semibold text-gray-800 mb-2">SET INDIA BUSINESS PVT LTD</p>
            <p className="text-gray-700 text-sm">Email: support@kopartner.in</p>
            <p className="text-gray-700 text-sm mb-3">Website: www.kopartner.in</p>
            <p className="text-gray-600 text-sm">
              For refund-related queries, please include your booking ID and registered phone number in your email.
            </p>
          </div>

          {/* Acknowledgment */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-lg font-bold mb-3 text-red-700">Acknowledgment</h2>
            <p className="text-red-800 text-sm leading-relaxed">
              By using KoPartner and making any payments, you acknowledge that you have read, understood, and agree to this Refund Policy in its entirety. You specifically acknowledge that <strong>membership fees are non-refundable</strong> and that <strong>all service refunds are credited to the KoPartner Wallet only</strong> for use on future bookings.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RefundPage;
